import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Link } from "react-router-dom";
import type { YearlySpendBreakdown } from "@/lib/fuel-analytics";
import { fuelMonthPath } from "@/lib/fuel-analytics";
import { GlassDonutChart } from "@/components/charts/GlassDonutChart";
import { BRAND, CHART_COLORS } from "@/lib/brand-colors";
import { formatCurrency, formatFuelPrice, formatNumber } from "@/lib/utils";

const BAR_ANIMATION = { animationDuration: 900, animationEasing: "ease-out" as const };

function BreakdownTable({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; value: string; meta?: string; href?: string }[];
}) {
  if (rows.length === 0) return null;

  return (
    <div className="rounded-2xl bg-canvas p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted">{title}</p>
      <div className="mt-3 space-y-2">
        {rows.map((row) => {
          const inner = (
            <>
              <div className="min-w-0">
                <p className="truncate font-semibold text-foreground">{row.label}</p>
                {row.meta ? <p className="truncate text-xs text-muted">{row.meta}</p> : null}
              </div>
              <p className="shrink-0 font-bold text-foreground">{row.value}</p>
            </>
          );

          if (row.href) {
            return (
              <Link
                key={row.label}
                to={row.href}
                className="flex items-start justify-between gap-3 rounded-xl p-2 text-sm transition hover:bg-surface"
              >
                {inner}
              </Link>
            );
          }

          return (
            <div key={row.label} className="flex items-start justify-between gap-3 text-sm">
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

type Props = {
  breakdown: YearlySpendBreakdown;
  scope?: "platform" | "user";
  basePath?: string;
};

export function YearlySpendBreakdownView({ breakdown, scope = "platform", basePath }: Props) {
  const { overview, year, isCurrentYear, monthRows, spendByMonth, spendByGrade, spendByUser, spendByCar, spendByStation } =
    breakdown;

  const spendLabel = isCurrentYear ? "Year-to-date spend" : "Total spend";

  return (
    <div className="space-y-5">
      <div className="stagger-children grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl bg-canvas p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">{spendLabel}</p>
          <p className="mt-2 text-2xl font-extrabold text-foreground">{formatCurrency(overview.totalSpent)}</p>
        </div>
        <div className="rounded-2xl bg-canvas p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Fill-ups</p>
          <p className="mt-2 text-2xl font-extrabold text-foreground">{formatNumber(overview.totalFillUps)}</p>
        </div>
        <div className="rounded-2xl bg-canvas p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Litres</p>
          <p className="mt-2 text-2xl font-extrabold text-foreground">
            {formatNumber(Math.round(overview.totalLitres))} L
          </p>
        </div>
        <div className="rounded-2xl bg-canvas p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Avg price</p>
          <p className="mt-2 text-2xl font-extrabold text-foreground">
            {formatFuelPrice(overview.avgPricePerLitre)}/L
          </p>
          <p className="mt-1 text-xs text-muted">{formatCurrency(overview.avgFillCost)} per fill</p>
        </div>
      </div>

      {breakdown.vsPriorYearPercent != null && breakdown.priorYearSpent != null ? (
        <p className="rounded-2xl border border-border bg-canvas px-4 py-3 text-sm text-muted">
          {breakdown.vsPriorYearPercent >= 0 ? "Up" : "Down"}{" "}
          {Math.abs(breakdown.vsPriorYearPercent)}% vs{" "}
          {isCurrentYear ? "the same period last year" : `full year ${year - 1}`} (
          {formatCurrency(breakdown.priorYearSpent)}).
        </p>
      ) : null}

      <div className="rounded-3xl bg-canvas p-4 sm:p-5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Monthly spend</p>
        <p className="mt-0.5 text-xs text-muted">How {year} spend is distributed month by month</p>
        <div className="mt-4 h-[240px] w-full">
          {spendByMonth.some((entry) => entry.value > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spendByMonth} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8ed" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#86868b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#86868b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(value) => formatCurrency(Number(value))}
                  contentStyle={{ borderRadius: 12, borderColor: "#e8e8ed" }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={48} fill={BRAND.accent} {...BAR_ANIMATION} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted">No monthly spend data.</p>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl bg-canvas p-4 sm:p-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Spend by grade</p>
          <div className="mt-4 h-[220px]">
            {spendByGrade.length > 0 ? (
              <GlassDonutChart data={spendByGrade} valueLabel="JMD" maxSlices={6} minLabelPercent={0.03} />
            ) : (
              <p className="flex h-full items-center justify-center text-sm text-muted">No grade data.</p>
            )}
          </div>
        </div>

        <BreakdownTable
          title="Month-by-month"
          rows={monthRows.map((row) => ({
            label: row.month,
            value: formatCurrency(row.spent),
            meta: `${row.fillUps} fill-ups · ${formatNumber(row.litres)} L`,
            href: basePath ? fuelMonthPath(basePath, breakdown.year, row.monthIndex) : undefined,
          }))}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {scope === "platform" ? (
          <BreakdownTable
            title="Top users by spend"
            rows={spendByUser.map((entry) => ({
              label: entry.name,
              value: formatCurrency(entry.value),
            }))}
          />
        ) : (
          <BreakdownTable
            title="Spend by vehicle"
            rows={spendByCar.map((entry) => ({
              label: entry.name,
              value: formatCurrency(entry.value),
            }))}
          />
        )}

        <BreakdownTable
          title="Top stations by spend"
          rows={spendByStation.map((entry) => ({
            label: entry.name,
            value: formatCurrency(entry.value),
          }))}
        />
      </div>

      {scope === "user" && spendByCar.length > 1 ? (
        <div className="rounded-3xl bg-canvas p-4 sm:p-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Vehicle spend split</p>
          <div className="mt-4 h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spendByCar} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8ed" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#86868b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#86868b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={52} {...BAR_ANIMATION}>
                  {spendByCar.map((_, index) => (
                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}
    </div>
  );
}
